import { button, icon } from '../../tokens/component';
import { state, font as fontSemantic, spacing } from '../../tokens/semantic';
import font from '../../tokens/foundation/font';

const MuiButton = {
    defaultProps: {
        disableFocusRipple: true,
        disableRipple: true,
        variant: 'contained',
        size: 'medium'
    },
    styleOverrides: {
        root: ({ theme }) => ({
            textTransform: 'none',
            borderRadius: button.size.radius.text,
            fontWeight: font.weight.regular,
            lineHeight: 1,
            '&:focus-visible': {
                outline: `${state.outline.weight.focus.md} solid ${state.outline.color.focus}`,
                outlineOffset: state.outline.offset.focus.md,
                boxShadow: 'inherit'
            },
            '& .MuiButton-startIcon': {
                marginLeft: spacing.none
            },
            '&.Mui-disabled': {
                opacity: state.opacity.disabled,
            },
            [theme.breakpoints.down('sm')]: {
                height: button.size.lg,
                padding: `0 ${button.spacing.padding.lg}`,
                fontSize: fontSemantic.desktop.button.lg,
                '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
                    fontSize: icon.size.md,
                },
            }
        }),
        sizeSmall: {
            height: button.size.sm,
            padding: `0 ${button.spacing.padding.sm}`,
            fontSize: fontSemantic.desktop.button.sm,
            '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
                fontSize: icon.size.xs,
            }
        },
        sizeMedium: {
            height: button.size.md,
            padding: `0 ${button.spacing.padding.md}`,
            fontSize: fontSemantic.desktop.button.md,
            '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
                fontSize: icon.size.sm,
            }
        },
        sizeLarge: {
            height: button.size.lg,
            padding: `0 ${button.spacing.padding.lg}`,
            fontSize: fontSemantic.desktop.button.lg,
            '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
                fontSize: icon.size.md,
            }
        },
        containedPrimary: {
            backgroundColor: button.color.filled.bg.primary.default,
            '&:hover': {
                backgroundColor: button.color.filled.bg.primary.hover,
                boxShadow: button.effects.shadow.primary.default,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.primary.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.primary.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.primary.default
        },
        containedSecondary: {
            backgroundColor: button.color.filled.bg.secondary.default,
            border: `1px solid ${button.color.filled.border.secondary}`,
            color: button.color.filled.content.secondary,
            '&:hover': {
                backgroundColor: button.color.filled.bg.secondary.hover,
                boxShadow: button.effects.shadow.secondary.default,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.secondary.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.secondary.disabled,
                color: button.color.filled.content.secondary,
            },
            boxShadow: button.effects.shadow.secondary.default
        },
        containedError: {
            backgroundColor: button.color.filled.bg.destructive.default,
            '&:hover': {
                backgroundColor: button.color.filled.bg.destructive.hover,
                boxShadow: button.effects.shadow.destructive.default,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.destructive.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.destructive.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.destructive.default
        },
        containedSuccess: {
            backgroundColor: button.color.filled.bg.accent.default,
            '&:hover': {
                backgroundColor: button.color.filled.bg.accent.hover,
                boxShadow: button.effects.shadow.accent.default,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.accent.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.accent.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.accent.default
        },
        containedWarning: {
            backgroundColor: button.color.filled.bg.warning.default,
            '&:hover': {
                backgroundColor: button.color.filled.bg.warning.hover,
                boxShadow: button.effects.shadow.warning.default,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.warning.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.warning.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.warning.default
        },
    },
    variants: [
        {
            props: { variant: 'tertiary' },
            style: {
                color: button.color.ghost.content.tertiary.default,
                backgroundColor: 'transparent',
                '&:hover': {
                    backgroundColor: button.color.ghost.bg.hover,
                    boxShadow: 'none',
                },
                '&:active': {
                    backgroundColor: button.color.ghost.bg.active,
                },
                '&.Mui-disabled': {
                    color: button.color.ghost.content.tertiary.disabled,
                }
            },
        },
        {
            props: { variant: 'stateful'},
            style: {
                backgroundColor: button.color.stateful.bg.inactive.default,
                color: button.color.stateful.content.inactive,
                '&:hover': {
                    backgroundColor: button.color.stateful.bg.inactive.hover,
                },
                '&.active': {
                    backgroundColor: button.color.stateful.bg.active.default,
                    color: button.color.stateful.content.active,
                    fontWeight: font.weight.medium,
                    '&:hover': {
                        backgroundColor: button.color.stateful.bg.active.hover,
                    },
                }
            }
        }
    ]
};

export default MuiButton;